"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ShoppingBag, Star, TicketIcon, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { usePathname, useRouter } from "next/navigation";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getBookingContactDetails } from "@/actions/booking-contact-details";
import {
  dismissEventAddonPrompt,
  getEventAddonPromptCards,
} from "@/actions/event-addons";
import {
  getLiveEventCards,
  getPostEventReviewCards,
  type LiveEventCard,
  type LiveEventCardMedia,
  type PostEventReviewCard,
} from "@/actions/live-event-card";
import { BookingContactFields } from "@/components/contact-details/booking-contact-fields.client";
import { ContactEmailDrawer } from "@/components/contact-email/contact-email-drawer";
import { AppImage as Image } from "@/components/ui/app-image";
import { PosterVideo } from "@/components/ui/poster-video";
import {
  AddonThumb,
  formatAddonDeadlineShort,
} from "@/features/event-addons/components/addon-parts.client";
import { useAppForeground } from "@/hooks/use-app-foreground";
import { useAuthState } from "@/hooks/use-auth-state";
import { useBackAwarePush } from "@/hooks/use-back-aware-push";
import { useModalOverlayOpen } from "@/hooks/use-modal-overlay-open";
import type { AddonPromptCard } from "@/lib/events/addon-types";
import { localDayKey } from "@/lib/events/live-event-card-window";
import { cn } from "@/lib/utils";

/**
 * The day-of ticket, surfaced above the tab bar.
 *
 * ## Two kinds of "no"
 *
 * The X is a *not now*: it clears until the next time the app is opened, which
 * is exactly what somebody means when they swat a banner away mid-scroll. "Don't
 * show this again" is the real no, and it is remembered forever — in
 * `localStorage`, keyed by booking, because a preference this small does not
 * justify a column and a migration, and losing it on a new device is harmless
 * (the event will be over).
 *
 * The X animates the card into the wallet icon rather than fading it out. A
 * banner that vanishes teaches nothing; a banner that visibly flies somewhere
 * teaches where to look for it — the same trick the Instants camera plays when a
 * snap flies into the vault.
 *
 * ## Why it does not need its own dismissal API
 *
 * The three conditions that permanently retire the card — the event ending,
 * every ticket being scanned in, and "don't show again" — are already known
 * without one. The first two come back from `getLiveEventCards` on every
 * foreground; only the third is local.
 */

/**
 * Light liquid glass: a translucent tint over a blurred, saturated backdrop,
 * with a lit top edge. Kept fairly opaque — these cards carry text the
 * person must read at a glance, over any page.
 */
const CARD_CHROME = cn(
  "relative overflow-hidden rounded-[22px] border",
  "border-white/60 bg-white/72 backdrop-blur-2xl backdrop-saturate-150",
  "shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_18px_40px_-8px_rgba(15,23,42,0.28)]",
  "dark:border-white/14 dark:bg-neutral-900/62",
  "dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_18px_40px_-6px_rgba(0,0,0,0.6)]",
);

/** The specular falloff across the top half — what makes the tint read as glass. */
function CardSheen() {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/45 to-transparent dark:from-white/[0.07]"
    />
  );
}

const LIVE_HIDDEN_KEY = "hiive.live-event-card.hidden";
const REVIEW_HIDDEN_KEY = "hiive.post-event-review-card.hidden:v1";
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const EMPTY_LIVE_CARDS: LiveEventCard[] = [];
const EMPTY_REVIEW_CARDS: PostEventReviewCard[] = [];
const EMPTY_ADDON_CARDS: AddonPromptCard[] = [];

function readHiddenIds(key: string): number[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is number => typeof value === "number")
      : [];
  } catch {
    // Private mode, cleared storage, or a value written by an older revision.
    // "We don't know what they hid" is a fine state — the card simply shows.
    return [];
  }
}

function persistHiddenId(key: string, id: number) {
  if (typeof window === "undefined") return;
  try {
    const next = [...new Set([...readHiddenIds(key), id])].slice(-50);
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // Nothing to do — the in-memory dismissal still holds for this session.
  }
}

type Flight = {
  from: DOMRect;
  to: { x: number; y: number };
};

function formatWhen(startIso: string, endIso: string, timeZone: string) {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const now = Date.now();
  const time = start.toLocaleTimeString("en-IN", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  });

  if (now >= start.getTime() && now <= end.getTime()) return "Happening now";

  const minutesAway = Math.round((start.getTime() - now) / 60_000);
  if (minutesAway <= 90 && minutesAway > 0)
    return `Starts in ${minutesAway} min`;

  const isToday =
    localDayKey(start, timeZone) === localDayKey(new Date(now), timeZone);
  return isToday ? `Today at ${time}` : `Tomorrow at ${time}`;
}

function ReviewHiiveLogo({ candidate }: { candidate: PostEventReviewCard }) {
  return (
    <span className="relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-black/5 text-sm font-semibold text-text-secondary dark:bg-white/10 dark:text-white/70">
      {candidate.hiiveLogoUrl ? (
        <Image
          src={candidate.hiiveLogoUrl}
          alt=""
          fill
          sizes="40px"
          className="object-cover"
        />
      ) : (
        candidate.hiiveName.slice(0, 1).toUpperCase()
      )}
    </span>
  );
}

function EventCardArtwork({
  media,
  eventName,
  kind,
}: {
  media: LiveEventCardMedia | null;
  eventName: string;
  kind: "ticket" | "review";
}) {
  const fallback =
    kind === "ticket" ? (
      <TicketIcon className="size-5" aria-hidden="true" />
    ) : (
      <Star className="size-5" aria-hidden="true" />
    );

  if (!media) {
    return (
      <span
        className={cn(
          "relative flex size-14 shrink-0 items-center justify-center rounded-2xl",
          kind === "ticket"
            ? "bg-brand/15 text-brand"
            : "bg-amber-400/15 text-amber-500 dark:text-amber-300",
        )}
      >
        {fallback}
      </span>
    );
  }

  // Fit the intrinsic ratio into a 68px-high / 124px-wide box. Both dimensions
  // are derived from the same ratio, so portrait artwork and landscape video
  // are never squeezed to match the card chrome.
  const ratio =
    Number.isFinite(media.aspectRatio) && media.aspectRatio > 0
      ? media.aspectRatio
      : 4 / 5;
  const maxHeight = 68;
  const maxWidth = 124;
  const width = Math.min(maxWidth, maxHeight * ratio);
  const height = width / ratio;

  return (
    <span
      className="relative shrink-0 overflow-hidden rounded-2xl bg-black/10 shadow-sm dark:bg-white/5"
      style={{ width, height }}
    >
      {media.kind === "video" ? (
        <PosterVideo
          src={media.url}
          mimeType={media.mimeType}
          posterUrl={media.posterUrl ?? undefined}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-label={`${eventName} video`}
        />
      ) : (
        <Image
          src={media.url}
          alt={`${eventName} event artwork`}
          fill
          className="object-cover"
          sizes="124px"
        />
      )}
      <span
        className={cn(
          "absolute right-1.5 bottom-1.5 flex size-7 items-center justify-center rounded-xl border border-white/20 bg-black/65 text-white shadow-sm backdrop-blur-md",
          kind === "review" && "text-amber-300",
        )}
      >
        {fallback}
      </span>
    </span>
  );
}

/**
 * Reports the current path up to the host. Its own Suspense boundary because
 * the host renders in the app shell of every route, and reading request data
 * there outside Suspense fails the prerender of every static page.
 */
function PathnameReporter({ onChange }: { onChange: (path: string) => void }) {
  const pathname = usePathname();
  useEffect(() => {
    onChange(pathname);
  }, [onChange, pathname]);
  return null;
}

export function LiveEventCardHost() {
  const router = useRouter();
  const [pathname, setPathname] = useState("");
  // The card floats over every page; its ticket's back arrow returns there.
  const backAwarePush = useBackAwarePush();
  const queryClient = useQueryClient();
  const { authStatus } = useAuthState();
  const isOverlayOpen = useModalOverlayOpen();

  // Dismissed for this app session only. Plain state, not sessionStorage: in a
  // native shell the page is never reloaded, so storage-backed state would
  // outlive the app "reopening" and the card would stay gone for days.
  const [snoozedIds, setSnoozedIds] = useState<number[]>([]);
  const [hiddenIds, setHiddenIds] = useState<number[]>([]);
  const [snoozedReviewIds, setSnoozedReviewIds] = useState<number[]>([]);
  const [hiddenReviewIds, setHiddenReviewIds] = useState<number[]>([]);
  // Add-on reminders: "not now" is session state like the others; "don't show
  // again" is remembered server-side (the last-call push honours it too), so
  // this list only bridges the moment before the next refetch.
  const [snoozedAddonIds, setSnoozedAddonIds] = useState<number[]>([]);
  const [hiddenAddonIds, setHiddenAddonIds] = useState<number[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState<string | null>(null);
  const [flight, setFlight] = useState<Flight | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const flightTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setHiddenIds(readHiddenIds(LIVE_HIDDEN_KEY));
    setHiddenReviewIds(readHiddenIds(REVIEW_HIDDEN_KEY));
    return () => {
      if (flightTimerRef.current) window.clearTimeout(flightTimerRef.current);
    };
  }, []);

  const enabled = authStatus === "authenticated";

  const { data: eventCards, refetch } = useQuery({
    queryKey: ["event-context-cards"],
    queryFn: async () => {
      const [liveResult, reviewResult, addonResult] = await Promise.all([
        getLiveEventCards(),
        getPostEventReviewCards(),
        getEventAddonPromptCards(),
      ]);
      return {
        live: liveResult.ok ? liveResult.data : EMPTY_LIVE_CARDS,
        reviews: reviewResult.ok ? reviewResult.data : EMPTY_REVIEW_CARDS,
        addons: addonResult.ok ? addonResult.data : EMPTY_ADDON_CARDS,
      };
    },
    enabled,
    staleTime: 60_000,
    refetchInterval: (query) => {
      // This callback is also evaluated while Next prerenders the shell. A
      // request-time clock there would make every static route dynamic; in the
      // browser, timeOrigin + the monotonic clock gives us the same epoch.
      if (typeof window === "undefined") return POLL_INTERVAL_MS;
      const now = window.performance.timeOrigin + window.performance.now();
      const nextEnd = query.state.data?.live
        .map((candidate) => new Date(candidate.endTimeIso).getTime())
        .filter((end) => Number.isFinite(end) && end > now)
        .sort((a, b) => a - b)[0];

      // Retire a stale ticket and reveal its review prompt at the event's exact
      // end instead of waiting for the ordinary five-minute refresh.
      return nextEnd
        ? Math.min(POLL_INTERVAL_MS, Math.max(250, nextEnd - now + 50))
        : POLL_INTERVAL_MS;
    },
    refetchOnWindowFocus: true,
  });

  // "Every time they open the app" — a swat lasts until the next foreground,
  // and the card re-reads check-in state at the same moment.
  useAppForeground(() => {
    setSnoozedIds([]);
    setSnoozedReviewIds([]);
    setSnoozedAddonIds([]);
    setExpanded(false);
    void refetch();
  });

  const card = useMemo(
    () =>
      (eventCards?.live ?? EMPTY_LIVE_CARDS).find(
        (candidate) =>
          !candidate.checkedIn &&
          !snoozedIds.includes(candidate.bookingId) &&
          !hiddenIds.includes(candidate.bookingId),
      ) ?? null,
    [eventCards?.live, hiddenIds, snoozedIds],
  );

  // Second in line: an add-on menu still open on an upcoming ticket. Not on
  // the screens that already show that menu, where it would only repeat them.
  const addonCard = useMemo(
    () =>
      card
        ? null
        : ((eventCards?.addons ?? EMPTY_ADDON_CARDS).find(
            (candidate) =>
              !snoozedAddonIds.includes(candidate.occurrenceId) &&
              !hiddenAddonIds.includes(candidate.occurrenceId) &&
              pathname !== `/wallet/events/${candidate.bookingId}` &&
              !pathname.startsWith("/shop/"),
          ) ?? null),
    [card, eventCards?.addons, hiddenAddonIds, snoozedAddonIds, pathname],
  );

  // A ticket for something happening now always wins the single available
  // slot above navigation. The review comes back once the others retire.
  const reviewCard = useMemo(
    () =>
      card || addonCard
        ? null
        : ((eventCards?.reviews ?? EMPTY_REVIEW_CARDS).find(
            (candidate) =>
              !snoozedReviewIds.includes(candidate.hiiveId) &&
              !hiddenReviewIds.includes(candidate.hiiveId),
          ) ?? null),
    [card, addonCard, eventCards?.reviews, hiddenReviewIds, snoozedReviewIds],
  );
  const reviewGroup = useMemo(
    () =>
      reviewCard
        ? (eventCards?.reviews ?? EMPTY_REVIEW_CARDS).filter(
            (candidate) =>
              candidate.eventId === reviewCard.eventId &&
              !snoozedReviewIds.includes(candidate.hiiveId) &&
              !hiddenReviewIds.includes(candidate.hiiveId),
          )
        : EMPTY_REVIEW_CARDS,
    [reviewCard, eventCards?.reviews, snoozedReviewIds, hiddenReviewIds],
  );

  const { data: contact, refetch: refetchContact } = useQuery({
    queryKey: ["booking-contact-details"],
    queryFn: async () => {
      const result = await getBookingContactDetails();
      return result.ok ? result.data : null;
    },
    enabled: enabled && Boolean(card),
    staleTime: 60_000,
  });

  const startFlight = useCallback((bookingId: number) => {
    const node = cardRef.current;
    // The wallet icon in the app header is where the ticket actually lives. When
    // the current route has no header, the top-right corner is the honest
    // approximation — it is the direction the icon is in.
    const target = document.querySelector<HTMLElement>(
      "[data-wallet-flight-target]",
    );
    if (node) {
      const from = node.getBoundingClientRect();
      const targetRect = target?.getBoundingClientRect();
      setFlight({
        from,
        to: targetRect
          ? {
              x: targetRect.left + targetRect.width / 2,
              y: targetRect.top + targetRect.height / 2,
            }
          : { x: window.innerWidth - 32, y: 40 },
      });
    }
    // A hard deadline as well as the animation's own completion callback. The
    // ghost is decoration, but `onAnimationComplete` only fires if frames are
    // actually being served — and on a throttled tab or a backgrounded WebView
    // they are not, which would strand a full-size copy of the card on screen.
    if (flightTimerRef.current) window.clearTimeout(flightTimerRef.current);
    flightTimerRef.current = window.setTimeout(() => setFlight(null), 700);

    setSnoozedIds((current) => [...current, bookingId]);
    setExpanded(false);
  }, []);

  const hideForever = useCallback((bookingId: number) => {
    persistHiddenId(LIVE_HIDDEN_KEY, bookingId);
    setHiddenIds((current) => [...current, bookingId]);
    setExpanded(false);
  }, []);

  const openReview = useCallback(
    (candidate: PostEventReviewCard) => {
      setSnoozedReviewIds((current) => [...current, candidate.hiiveId]);
      router.push(
        candidate.groupSize > 1
          ? `/hiives/${candidate.hiiveSlug}/reviews/write?event=${candidate.eventId}`
          : `/hiives/${candidate.hiiveSlug}/reviews/write`,
      );
    },
    [router],
  );

  const hideAddonForever = useCallback((occurrenceId: number) => {
    setHiddenAddonIds((current) => [...current, occurrenceId]);
    void dismissEventAddonPrompt({ occurrenceId });
  }, []);

  const hideReviewForever = useCallback((hiiveId: number) => {
    persistHiddenId(REVIEW_HIDDEN_KEY, hiiveId);
    setHiddenReviewIds((current) => [...current, hiiveId]);
  }, []);

  if (!enabled)
    return (
      <Suspense fallback={null}>
        <PathnameReporter onChange={setPathname} />
      </Suspense>
    );

  return (
    <>
      <Suspense fallback={null}>
        <PathnameReporter onChange={setPathname} />
      </Suspense>
      {card && !isOverlayOpen ? (
        <div
          key={card.bookingId}
          ref={cardRef}
          className={cn(
            "pointer-events-auto fixed inset-x-4 z-90 mx-auto max-w-md",
            // Sits on the padding the shell already reserves for the tab bar,
            // so it lands just above the nav with a navbar and just above the
            // safe area without one — no route-aware offset to keep in sync.
            "bottom-[calc(var(--bottom-nav-layout-padding,0px)+0.5rem)]",
            // A CSS entry animation, deliberately not a JS-driven one. A card
            // whose opacity starts at 0 and is raised by rAF is invisible on any
            // frame-starved surface — a backgrounded WebView, a throttled tab —
            // and this is the one card somebody needs while standing at a door.
            // Keyframes end at the element's own styles, so a skipped animation
            // still leaves it fully painted.
            "animate-in duration-300 fade-in-0 slide-in-from-bottom-4",
          )}
        >
          <div className={CARD_CHROME}>
            <CardSheen />
            <div className="relative flex items-center gap-3 p-3.5">
              <button
                type="button"
                onClick={() =>
                  backAwarePush(`/wallet/events/${card.bookingId}`)
                }
                className="flex min-w-0 flex-1 items-center gap-3 text-left active:scale-[0.99]"
              >
                <EventCardArtwork
                  media={card.media}
                  eventName={card.eventName}
                  kind="ticket"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-[15px] font-semibold text-text-primary dark:text-white">
                      {card.eventName}
                    </span>
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-sm text-text-secondary dark:text-white/60">
                    <span className="truncate">
                      {formatWhen(
                        card.startTimeIso,
                        card.endTimeIso,
                        card.timeZone,
                      )}{" "}
                      · {card.venueName}
                    </span>
                  </span>
                </span>
                <ChevronRight
                  className="size-5 shrink-0 text-text-tertiary dark:text-white/45"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                aria-label="Hide until next time"
                onClick={() => startFlight(card.bookingId)}
                className="-mt-1 -mr-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-black/5 dark:text-white/55 dark:hover:bg-white/10"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>

            <div className="relative flex items-center justify-between gap-3 border-t border-black/8 px-3.5 py-2 dark:border-white/10">
              <button
                type="button"
                onClick={() => setExpanded((current) => !current)}
                className="text-sm font-semibold text-brand"
              >
                {expanded ? "Hide my details" : "My details for check-in"}
              </button>
              <button
                type="button"
                onClick={() => hideForever(card.bookingId)}
                className="text-xs text-text-tertiary dark:text-white/45"
              >
                Don't show again
              </button>
            </div>

            {expanded ? (
              <div className="relative border-t border-black/8 px-3.5 py-3.5 dark:border-white/10">
                <p className="text-sm text-text-secondary dark:text-white/60">
                  Read these out at the door. Make any changes, then save them.
                </p>
                {contact ? (
                  <BookingContactFields
                    initial={contact}
                    context="live_event_card"
                    saveMode="explicit"
                    className="mt-3"
                    onEmailVerificationRequired={setEmailToVerify}
                    onSaved={(details) => {
                      queryClient.setQueryData(
                        ["booking-contact-details"],
                        details,
                      );
                    }}
                  />
                ) : (
                  <div className="mt-3 h-24 animate-pulse rounded-xl bg-black/5 dark:bg-white/5" />
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {addonCard && !isOverlayOpen ? (
        <div
          key={`addon:${addonCard.occurrenceId}`}
          className={cn(
            "pointer-events-auto fixed inset-x-4 z-90 mx-auto max-w-md",
            "bottom-[calc(var(--bottom-nav-layout-padding,0px)+0.5rem)]",
            "animate-in duration-300 fade-in-0 slide-in-from-bottom-4",
          )}
        >
          <div className={CARD_CHROME}>
            <CardSheen />
            <div className="relative flex items-center gap-3 p-3.5">
              <button
                type="button"
                onClick={() => {
                  setSnoozedAddonIds((current) => [
                    ...current,
                    addonCard.occurrenceId,
                  ]);
                  backAwarePush(
                    `/wallet/events/${addonCard.bookingId}?addons=open`,
                  );
                }}
                className="flex min-w-0 flex-1 items-center gap-3 text-left active:scale-[0.99]"
              >
                {/* The event leads — it is what they booked; what they can
                    add rides along as small thumbs on its corner. */}
                <span className="relative shrink-0">
                  {addonCard.banner ? (
                    <AddonThumb
                      image={addonCard.banner}
                      alt=""
                      sizes="56px"
                      className="h-[68px] w-14 rounded-2xl shadow-sm"
                    />
                  ) : (
                    <span className="flex h-[68px] w-14 items-center justify-center rounded-2xl bg-brand/15 text-brand dark:bg-white/10 dark:text-white">
                      <ShoppingBag className="size-5" aria-hidden="true" />
                    </span>
                  )}
                  {addonCard.thumbs.length ? (
                    <span className="absolute -right-2 -bottom-1.5 flex -space-x-2">
                      {addonCard.thumbs.slice(0, 2).map((thumb) => (
                        <AddonThumb
                          key={thumb.url}
                          image={thumb}
                          alt=""
                          sizes="24px"
                          className="size-6 rounded-lg ring-2 ring-white dark:ring-neutral-900"
                        />
                      ))}
                    </span>
                  ) : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-[15px] leading-snug font-semibold text-text-primary dark:text-white">
                    Pre-order{" "}
                    {addonCard.titles
                      .map((title) => title.toLowerCase())
                      .join(" · ")}
                  </span>
                  <span className="mt-0.5 block truncate text-sm text-text-secondary dark:text-white/65">
                    By {formatAddonDeadlineShort(addonCard.cutoffIso)} ·{" "}
                    {addonCard.eventName}
                  </span>
                </span>
                <ChevronRight
                  className="size-5 shrink-0 text-text-tertiary dark:text-white/45"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                aria-label="Hide until next time"
                onClick={() =>
                  setSnoozedAddonIds((current) => [
                    ...current,
                    addonCard.occurrenceId,
                  ])
                }
                className="-mt-1 -mr-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-black/5 dark:text-white/55 dark:hover:bg-white/10"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
            <div className="relative flex items-center justify-between gap-3 border-t border-black/8 px-3.5 py-2 dark:border-white/10">
              <button
                type="button"
                onClick={() => {
                  setSnoozedAddonIds((current) => [
                    ...current,
                    addonCard.occurrenceId,
                  ]);
                  backAwarePush(
                    `/wallet/events/${addonCard.bookingId}?addons=open`,
                  );
                }}
                className="text-sm font-semibold text-brand dark:text-white"
              >
                {addonCard.actionLabel}
              </button>
              <button
                type="button"
                onClick={() => hideAddonForever(addonCard.occurrenceId)}
                className="text-xs text-text-tertiary dark:text-white/45"
              >
                Don't show again
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {reviewCard && !isOverlayOpen ? (
        <div
          key={`review:${reviewCard.eventId}`}
          className={cn(
            "pointer-events-auto fixed inset-x-4 z-90 mx-auto max-w-md",
            "bottom-[calc(var(--bottom-nav-layout-padding,0px)+0.5rem)]",
            "animate-in duration-300 fade-in-0 slide-in-from-bottom-4",
          )}
        >
          <div className={CARD_CHROME}>
            <CardSheen />
            {reviewGroup.length > 1 ? (
              <>
                <div className="relative flex items-center gap-3 p-3.5 pb-2.5">
                  <EventCardArtwork
                    media={reviewCard.media}
                    eventName={reviewCard.eventName}
                    kind="review"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold text-text-primary dark:text-white">
                      How was {reviewCard.eventName}?
                    </span>
                    <span className="mt-0.5 block text-sm text-text-secondary dark:text-white/60">
                      {reviewCard.groupSize} Hiives ran this together. Review
                      any of them.
                    </span>
                  </span>
                  <button
                    type="button"
                    aria-label="Hide until next time"
                    onClick={() =>
                      setSnoozedReviewIds((current) => [
                        ...current,
                        ...reviewGroup.map((item) => item.hiiveId),
                      ])
                    }
                    className="-mt-1 -mr-1 flex size-9 shrink-0 items-center justify-center self-start rounded-full text-text-tertiary transition-colors hover:bg-black/5 dark:text-white/55 dark:hover:bg-white/10"
                  >
                    <X className="size-5" aria-hidden="true" />
                  </button>
                </div>
                <ul className="relative divide-y divide-black/6 border-t border-black/8 dark:divide-white/8 dark:border-white/10">
                  {reviewGroup.map((candidate) => (
                    <li
                      key={candidate.hiiveId}
                      className="flex items-center gap-3 px-3.5 py-2.5"
                    >
                      <button
                        type="button"
                        onClick={() => openReview(candidate)}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left active:scale-[0.99]"
                      >
                        <ReviewHiiveLogo candidate={candidate} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-text-primary dark:text-white">
                            {candidate.hiiveName}
                          </span>
                          <span className="block truncate text-xs text-text-tertiary dark:text-white/50">
                            {candidate.isCollaborator
                              ? "Also in collaboration"
                              : "Host"}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-0.5 text-sm font-semibold text-brand">
                          Review
                          <ChevronRight className="size-4" aria-hidden="true" />
                        </span>
                      </button>
                      <button
                        type="button"
                        aria-label={`Don't ask me to review ${candidate.hiiveName} again`}
                        onClick={() => hideReviewForever(candidate.hiiveId)}
                        className="shrink-0 rounded-full px-2 py-1 text-xs text-text-tertiary hover:bg-black/5 dark:text-white/45 dark:hover:bg-white/10"
                      >
                        Skip
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <div className="relative flex items-center gap-3 p-3.5">
                  <button
                    type="button"
                    onClick={() => openReview(reviewCard)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left active:scale-[0.99]"
                  >
                    <EventCardArtwork
                      media={reviewCard.media}
                      eventName={reviewCard.eventName}
                      kind="review"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-text-primary dark:text-white">
                        How was {reviewCard.eventName}?
                      </span>
                      <span className="mt-0.5 block truncate text-sm text-text-secondary dark:text-white/60">
                        {reviewCard.isCollaborator
                          ? `Review ${reviewCard.hiiveName} · also in collaboration`
                          : `Review ${reviewCard.hiiveName}`}
                      </span>
                    </span>
                    <ChevronRight
                      className="size-5 shrink-0 text-text-tertiary dark:text-white/45"
                      aria-hidden="true"
                    />
                  </button>
                  <button
                    type="button"
                    aria-label="Hide until next time"
                    onClick={() =>
                      setSnoozedReviewIds((current) => [
                        ...current,
                        reviewCard.hiiveId,
                      ])
                    }
                    className="-mt-1 -mr-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-black/5 dark:text-white/55 dark:hover:bg-white/10"
                  >
                    <X className="size-5" aria-hidden="true" />
                  </button>
                </div>
                <div className="relative flex items-center justify-between gap-3 border-t border-black/8 px-3.5 py-2 dark:border-white/10">
                  <button
                    type="button"
                    onClick={() => openReview(reviewCard)}
                    className="text-sm font-semibold text-brand"
                  >
                    Write a review
                  </button>
                  <button
                    type="button"
                    onClick={() => hideReviewForever(reviewCard.hiiveId)}
                    className="text-xs text-text-tertiary dark:text-white/45"
                  >
                    Don't show again
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {/*
        The card's flight into the wallet. Rendered as a separate fixed layer so
        it survives the card unmounting, and marked aria-hidden — it is pure
        orientation, and a screen reader has already been told the card closed.
      */}
      <AnimatePresence>
        {flight ? (
          <motion.div
            key="live-event-card-flight"
            aria-hidden="true"
            className="pointer-events-none fixed z-100 rounded-[22px] border border-black/10 bg-white/85 backdrop-blur-xl dark:border-white/12 dark:bg-black/78"
            initial={{
              left: flight.from.left,
              top: flight.from.top,
              width: flight.from.width,
              height: flight.from.height,
              opacity: 1,
              borderRadius: 22,
            }}
            animate={{
              left: flight.to.x - 14,
              top: flight.to.y - 14,
              width: 28,
              height: 28,
              opacity: 0,
              borderRadius: 14,
            }}
            transition={{ duration: 0.52, ease: [0.32, 0, 0.24, 1] }}
            onAnimationComplete={() => setFlight(null)}
          />
        ) : null}
      </AnimatePresence>

      <ContactEmailDrawer
        open={Boolean(emailToVerify)}
        onOpenChange={(open) => {
          if (!open) setEmailToVerify(null);
        }}
        context="wallet_ticket"
        eventBookingId={card?.bookingId ?? null}
        initialEmail={emailToVerify}
        title="Confirm your new email"
        description="Your current email is already confirmed. Verify the new one before replacing it."
        onVerified={() => {
          setEmailToVerify(null);
          void refetchContact();
        }}
      />
    </>
  );
}
